import type { SortState } from '@/lib/tableSort';

/** Overview columns the backend can sort, in header order. Actions stays plain. */
const GT_SORT_COLUMNS = [
    'file',
    'splitting',
    'classification',
    'extraction',
    'updated',
] as const;

export type GtSortColumn = (typeof GT_SORT_COLUMNS)[number];

/** `?sort` and the backend's `GTSortFilter`: `<column>_<asc|desc>`. */
export type GtSortOption = `${GtSortColumn}_${'asc' | 'desc'}`;

export const GT_SORT_OPTIONS = GT_SORT_COLUMNS.flatMap((column) => [
    `${column}_asc` as const,
    `${column}_desc` as const,
]) satisfies GtSortOption[];

/** The overview's order when `?sort` is absent: newest upload first. */
export const DEFAULT_GT_SORT: GtSortOption = 'updated_desc';

/** `?sort` as header sort state; absent is the default order, shown unsorted. */
export function gtSortToState(
    option: GtSortOption | undefined,
): SortState<GtSortColumn> | null {
    if (!option) return null;
    const separator = option.lastIndexOf('_');
    return {
        key: option.slice(0, separator) as GtSortColumn,
        direction: option.slice(separator + 1) as SortState['direction'],
    };
}

/**
 * Header sort state as `?sort`. An explicit Uploaded ↓ is kept as
 * `updated_desc` (not dropped as the default) so the next click still
 * cycles back to the default order.
 */
export function gtSortFromState(
    sort: SortState<GtSortColumn> | null,
): GtSortOption | undefined {
    return sort ? `${sort.key}_${sort.direction}` : undefined;
}
